import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { FEATURE_FLAGS_QUERY_KEY } from './featureFlagsContext';

/**
 * Refetch-on-navigation half of D27 (the provider's `refetchOnWindowFocus`
 * is the other half; no polling). Mounted once inside the router (AppLayout)
 * because the provider itself sits above `RouterProvider` and has no access
 * to the location.
 *
 * Invalidation (not `refetch`) lets TanStack keep serving the last good map
 * while the background refresh runs — flags never flicker mid-navigation.
 *
 * The guard compares the *actual* pathname against the previously-seen one
 * rather than using a "first render" boolean: under React 19 dev StrictMode,
 * passive effects are double-invoked and refs PERSIST across the double-
 * invocation, so a `useRef(true)` flag flips to false on the first invocation
 * and the second invocation then fires the side-effect every time the
 * component re-mounts — which on a failing /features endpoint produces an
 * infinite invalidate → refetch → 404 → re-render → invalidate loop.
 */
export function FeatureFlagsRouteSync(): null {
  const location = useLocation();
  const queryClient = useQueryClient();
  const prevPathname = useRef(location.pathname);

  useEffect(() => {
    if (prevPathname.current === location.pathname) return;
    prevPathname.current = location.pathname;
    void queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
  }, [location.pathname, queryClient]);

  return null;
}
